import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import React, { ReactElement } from "react";
import { useFormContext } from "react-hook-form";

import IconBestClients from "@/icons/icon-best-clients";
import IconBirthday from "@/icons/icon-birthday";
import IconDiamond from "@/icons/icon-diamond";
import IconGift from "@/icons/icon-gift";
import IconHelp from "@/icons/icon-help";
import { useServerSideProps } from "~/contexts/server-side-props";
import useSubscriptions from "~/hooks/use-subscriptions";
import Editorials from "~/types/editorials";
import { assertEditorialType } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import Icon from "../common/icon";
import Checkbox from "../form/checkbox";

type CustomStyles = {
  privacyPolicy?: string;
  link?: string;
  underline?: string;
  loyaltyBox?: string;
  benefitList?: string;
  benefitItem?: string;
  benefitLabel?: string;
  benefitIcon?: string;
};

type CustomerFlagsProps = {
  styles: CustomStyles;
};

export default function CustomerFlags({ styles }: CustomerFlagsProps) {
  const t = useTranslations();
  const router = useRouter();
  const { availableSubscriptions } = useSubscriptions();
  const { serverSideProps } = useServerSideProps();

  const contentUtils = serverSideProps.contentUtils;
  assertEditorialType<Editorials.ContentUtils>(contentUtils, "contentUtils");

  const privacyPolicyLink = textUtils.sanitizeContentfulUrl(contentUtils.fields.privacyPolicyLink, router);
  const loyaltyTermsLink = textUtils.sanitizeContentfulUrl(contentUtils.fields.loyaltyTermsOfServiceLink, router);

  const {
    register,
    formState: { errors },
  } = useFormContext();

  const checkboxElements: ReactElement[] = [
    {
      key: "LoyaltyProgram",
      element: (
        <div className={styles.loyaltyBox}>
          <Checkbox
            {...register("LoyaltyProgram")}
            label={t.rich("generic.signup_loyalty_program", {
              br: () => <br />,
              loyaltyParagraph: (chunks) => <p>{chunks}</p>,
              strong: (chunks) => <strong>{chunks}</strong>,
              benefitList: (chunks) => <ul className={styles.benefitList}>{chunks}</ul>,
              benefit: (chunks) => <li className={styles.benefitItem}>{chunks}</li>,
              benefitLabel: (chunks) => <span className={styles.benefitLabel}>{chunks}</span>,
              svgIconServices: () => <Icon icon={IconHelp} className={styles.benefitIcon} />,
              svgIconBirthdayGift: () => <Icon icon={IconBirthday} className={styles.benefitIcon} />,
              svgIconWelcomeGift: () => <Icon icon={IconGift} className={styles.benefitIcon} />,
              svgIconSpecialGifts: () => <Icon icon={IconBestClients} className={styles.benefitIcon} />,
              svgIconVIPCare: () => <Icon icon={IconDiamond} className={styles.benefitIcon} />,
            })}
            error={errors.LoyaltyProgram?.message as string}
          />
        </div>
      ),
    },
    {
      key: "ProfilingOptIn",
      element: (
        <>
          <p>
            {t.rich("generic.profiling_purposes_with_link", {
              underline: (chunks) => {
                return <span className={styles.underline}>{chunks?.toString()}</span>;
              },
            })}
          </p>
          <Checkbox
            {...register("ProfilingOptIn")}
            label={t("generic.online_profiling_opt_in")}
            error={errors.ProfilingOptIn?.message as string}
          />
        </>
      ),
    },
    {
      key: "MarketingOptIn",
      element: (
        <Checkbox
          {...register("MarketingOptIn")}
          label={t("generic.marketing_opt_in")}
          error={errors.MarketingOptIn?.message as string}
        />
      ),
    },
    {
      key: "ThirdPartiesDataTranAuthOptIn",
      element: (
        <Checkbox
          {...register("ThirdPartiesDataTranAuthOptIn")}
          label={t("generic.third_parties_data_tran_auth_opt_in")}
          error={errors.ThirdPartiesDataTranAuthOptIn?.message as string}
        />
      ),
    },
  ]
    .filter((item) => availableSubscriptions?.find((s) => s.BackendID === item.key))
    .map((item) => item.element);

  return (
    <div className={styles.privacyPolicy}>
      <p>
        {t.rich("signUp.disclaimer", {
          privacyPolicyLink: (chunks) => {
            return (
              <Link href={privacyPolicyLink} className={styles.link} target="_blank">
                {chunks?.toString()}
              </Link>
            );
          },
          termsLink: (chunks) => {
            return (
              <Link href={loyaltyTermsLink} className={styles.link} target="_blank">
                {chunks?.toString()}
              </Link>
            );
          },
        })}
      </p>
      <>
        {checkboxElements.map((e, i) => {
          return <React.Fragment key={i}>{e}</React.Fragment>;
        })}
      </>
    </div>
  );
}
